import { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/AdminLayout';
import { carPricesApi, CarBrand } from '../../lib/carPricesApi';
import { Plus, Edit2, Trash2, Eye, EyeOff, Image as ImageIcon, X } from 'lucide-react';
import { ImageUpload } from '../../components/ImageUpload';

export function CarBrandsManagement() {
  const [brands, setBrands] = useState<CarBrand[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBrand, setEditingBrand] = useState<CarBrand | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    logo_url: '',
    description: '',
    is_active: true
  });

  useEffect(() => {
    loadBrands();
  }, []);

  const loadBrands = async () => {
    try {
      setLoading(true);
      const data = await carPricesApi.getBrands();
      setBrands(data);
    } catch (error) {
      console.error('Error loading brands:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      alert('Le nom de la marque est requis');
      return;
    }

    try {
      setSubmitting(true);

      if (editingBrand) {
        // FIX: Create a payload that only includes the name if it has changed.
        // This prevents the API from trying to regenerate the slug ("audi") 
        // which causes the "duplicate key" error when just updating the logo.
        const updatePayload: any = {
            logo_url: formData.logo_url,
            description: formData.description,
            is_active: formData.is_active
        };

        if (formData.name.trim() !== editingBrand.name) {
            updatePayload.name = formData.name;
        }

        await carPricesApi.updateBrand(editingBrand.id, updatePayload);
      } else {
        // For creation, we send everything
        await carPricesApi.createBrand(formData);
      }

      await loadBrands();
      closeModal();
    } catch (error) {
      console.error('Error saving brand:', error);
      alert('Erreur lors de l\'enregistrement de la marque.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette marque ?')) {
      return;
    }

    try {
      await carPricesApi.deleteBrand(id);
      await loadBrands();
    } catch (error) {
      console.error('Error deleting brand:', error);
      alert('Erreur lors de la suppression de la marque');
    }
  };

  const toggleActive = async (brand: CarBrand) => {
    try {
      await carPricesApi.updateBrand(brand.id, { is_active: !brand.is_active });
      await loadBrands();
    } catch (error) {
      console.error('Error toggling brand status:', error);
      alert('Erreur lors de la mise à jour du statut');
    }
  };

  const openModal = (brand?: CarBrand) => {
    if (brand) {
      setEditingBrand(brand);
      setFormData({
        name: brand.name,
        logo_url: brand.logo_url || '',
        description: brand.description || '',
        is_active: brand.is_active
      });
    } else {
      setEditingBrand(null);
      setFormData({
        name: '',
        logo_url: '',
        description: '',
        is_active: true
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingBrand(null);
  };

  const handleImageUpload = (url: string) => {
    setFormData(prev => ({ ...prev, logo_url: url }));
  };

  return (
    <AdminLayout currentPage="car-brands">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Gestion des Marques</h1>
            <p className="text-gray-600 text-sm mt-1">
              Gérez les marques de véhicules pour les prix de neuf
            </p>
          </div>
          <button
            onClick={() => openModal()}
            className="inline-flex items-center space-x-2 bg-gradient-to-r from-[#D4452C] to-[#b83a24] text-white px-4 py-2 rounded-lg hover:shadow-lg transition-all"
          >
            <Plus className="h-4 w-4" />
            <span>Nouvelle marque</span>
          </button>
        </div>

        {/* Brands List */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4452C] mx-auto mb-4"></div>
              <p className="text-gray-600">Chargement des marques...</p>
            </div>
          </div>
        ) : brands.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <ImageIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucune marque</h3>
            <p className="text-gray-600 mb-6">
              Commencez par ajouter votre première marque
            </p>
            <button
              onClick={() => openModal()}
              className="inline-flex items-center space-x-2 bg-[#D4452C] text-white px-4 py-2 rounded-lg hover:bg-[#b83a24] transition-colors"
            >
              <Plus className="h-4 w-4" />
              <span>Ajouter une marque</span>
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Logo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nom
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Statut
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {brands.map((brand) => (
                    <tr key={brand.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center p-1">
                          {brand.logo_url ? (
                            <img
                              src={brand.logo_url}
                              alt={brand.name}
                              className="max-w-full max-h-full object-contain"
                            />
                          ) : (
                            <span className="text-gray-500 text-xl font-bold">
                              {brand.name.charAt(0)}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{brand.name}</div>
                        <div className="text-xs text-gray-400">{brand.slug}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-500 truncate max-w-xs">
                          {brand.description || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          brand.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {brand.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => toggleActive(brand)}
                            className={`p-2 rounded-lg transition-colors ${
                              brand.is_active
                                ? 'text-green-600 hover:bg-green-50'
                                : 'text-gray-400 hover:bg-gray-100'
                            }`}
                            title={brand.is_active ? 'Désactiver' : 'Activer'}
                          >
                            {brand.is_active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                          </button>
                          <button
                            onClick={() => openModal(brand)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Modifier"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(brand.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Supprimer"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
                <h2 className="text-xl font-bold text-gray-900">
                  {editingBrand ? 'Modifier la marque' : 'Nouvelle marque'}
                </h2>
                <button
                  onClick={closeModal}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom de la marque *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4452C]"
                    required
                    placeholder="Ex: Renault"
                  />
                </div>

                {/* Logo */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Logo de la marque
                  </label>
                  <ImageUpload
                    onImageUploaded={handleImageUpload}
                    currentImage={formData.logo_url}
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4452C]"
                    rows={3}
                    placeholder="Description de la marque..."
                  />
                </div>

                {/* Active Status */}
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                    className="w-4 h-4 text-[#D4452C] border-gray-300 rounded focus:ring-[#D4452C]"
                  />
                  <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                    Activer la marque immédiatement
                  </label>
                </div>

                {/* Actions */}
                <div className="flex space-x-3 pt-4 border-t border-gray-200">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 bg-gradient-to-r from-[#D4452C] to-[#b83a24] text-white py-2 rounded-lg font-medium hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? 'Enregistrement...' : (editingBrand ? 'Mettre à jour' : 'Créer la marque')}
                  </button>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-6 py-2 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                  >
                    Annuler
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}